import React, { FC } from 'react';
import { ShortInfo } from '../product/short-info';
import { ContentContainerMax } from '../../layout/container';
import { TierPriceOverview } from '../tier-price-overview/tier-price-overview';
import { SelectedRentalStationSection } from '../selected-rental-station/selected-rental-station';
import { ReadableMultilineAddress } from '../../helper/readable-address';
import styled from '@emotion/styled';
import { mediaQueries, spacingCalculator } from '@digando/react-component-library';
import { ProductPageQuery, AllZonesFragment } from '../../@types/codegen/graphql';
import { useTranslation } from 'next-i18next';
import { Zones } from '../../zones/zones';
import TextWithImage, { TextWithImageProps } from '../../zones/layers/TextWithImage';

type DisplayProductProps = {
  zones: AllZonesFragment[];
  productPrice: ProductPageQuery['productPrice'];
  productBundle: ProductPageQuery['productBundle'];
};

export const DisplayProductDetailLayout: FC<DisplayProductProps> = ({
  productPrice,
  productBundle,
  zones,
}) => {
  const [t] = useTranslation();
  const selectedRentalStation = productBundle?.bundle?.selectedRentalStation ?? null;
  const textWithImageComponentProps = zones.find(zone => 'ComponentContentTextWithImage' === zone.__typename)

  const distance = (
    selectedRentalStation?.location?.distance
      ? ((selectedRentalStation?.location?.distance ?? 1) / 1000).toFixed(0).toString()
      : '');

  return (
    <>
      <ShortInfo
        productBundle={productBundle}
        productPrice={productPrice}
      />

      <SliceZoneContainer>
        <Zones zones={zones.filter(zone => 'ComponentContentUspProduct' === zone.__typename)} />
      </SliceZoneContainer>

      <PriceZoneContainer>
        <SliceZoneContainer>
          {textWithImageComponentProps && (
            <TextWithImage {...textWithImageComponentProps as TextWithImageProps} fitParentContainer={true}/>
          )}
        </SliceZoneContainer>

        <PriceInformationContainer>
          {0 < (productBundle?.bundle?.prices.length ?? 0) && (
            <TierPriceOverview prices={productBundle?.bundle?.prices ?? []} />
          )}
        </PriceInformationContainer>
      </PriceZoneContainer>

      {selectedRentalStation && (
        <SelectedRentalStationSection
          rentalStationId={selectedRentalStation.id}
          WorkingHoursSubtitle={t('working-hours-subtitle')}
          sectionTitle={t('details-selected-tenant-and-price')}
          partnerName={selectedRentalStation?.name ?? ''}
          distance={distance}
          CompanySubtitle={t('selected-tenant')}
          address={<ReadableMultilineAddress address={selectedRentalStation} />}
          openingHours={selectedRentalStation?.openingHours ?? ''}
          tenantLogo={selectedRentalStation?.tenant?.logo}
          tenantPageUrl={selectedRentalStation.tenant?.digandoUrl ?? ''}
        />
      )}
    </>
  );
};

const PriceZoneContainer = styled(ContentContainerMax)`
  margin-top: ${spacingCalculator(6)};

  @media ${mediaQueries.tablet} {
    margin-top: ${spacingCalculator(10)};
  }
`;

const SliceZoneContainer = styled.div`
  grid-column: auto / span 12;

  @media ${mediaQueries.laptop} {
    grid-column: auto / span 6;
  }
`;

const PriceInformationContainer = styled.div`
  grid-column: auto / span 12;

  @media ${mediaQueries.laptop} {
    grid-column: auto / span 6;
  }
`;
